import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, UserCheck, UserX, FolderKanban, CheckSquare, MessageSquare, AlertTriangle } from 'lucide-react';
import { identityApi, type IdentityConflict } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

/**
 * Shown once, when the Hub holds a profile under this person's email but a
 * different id — their auth account was recreated at some point.
 *
 * It asks rather than guessing, because the alternative reading is real: a
 * client that reassigns a departing employee's email address looks exactly the
 * same from here, and merging there would hand the new person somebody else's
 * work. Counts only, never titles, for the same reason.
 */
export function IdentityConflictScreen({
  conflict,
  onResolved,
}: {
  conflict: IdentityConflict;
  onResolved: () => void;
}) {
  const [choice, setChoice] = useState<'claim' | 'separate' | null>(null);

  const resolve = useMutation({
    mutationFn: (value: 'claim' | 'separate') => identityApi.resolveConflict(value),
    onSuccess: onResolved,
  });

  const counts = [
    { icon: FolderKanban, label: 'projects', value: conflict.projects },
    { icon: CheckSquare, label: 'tasks', value: conflict.tasks },
    { icon: MessageSquare, label: 'discussions', value: conflict.discussions },
  ].filter((c) => c.value > 0);

  const lastSeen = new Date(conflict.lastSeenAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 p-6">
      <div className="w-full max-w-lg">
        <h1 className="text-heading-3 text-secondary">Which profile is yours?</h1>
        <p className="mt-2 text-body-sm text-grey-500">
          There is already a Solid Hub profile using <strong className="text-secondary">{conflict.email}</strong>.
          Your sign-in is new, so we need to know whether that earlier profile is you before we show you anything.
        </p>

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={() => setChoice('claim')}
            aria-pressed={choice === 'claim'}
            className={cn(
              'w-full rounded-xl border p-4 text-left transition-colors',
              choice === 'claim' ? 'border-accent bg-accent/5' : 'border-grey-200 bg-white hover:bg-grey-50',
            )}
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-secondary">
              <UserCheck className="h-4 w-4 text-accent" />
              That profile is mine
            </span>
            <span className="mt-1 block text-xs text-grey-500">
              Its work moves onto this sign-in and nothing is lost.
              {counts.length > 0 ? ' It currently has:' : ' It has no work attached yet.'}
            </span>
            {counts.length > 0 && (
              <span className="mt-3 flex flex-wrap gap-3">
                {counts.map(({ icon: Icon, label, value }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 text-xs font-medium text-secondary ring-1 ring-grey-200"
                  >
                    <Icon className="h-3.5 w-3.5 text-grey-400" />
                    <span className="tabular-nums">{value}</span> {label}
                  </span>
                ))}
              </span>
            )}
            <span className="mt-2 block text-xs text-grey-400">Last used {lastSeen}</span>
          </button>

          <button
            type="button"
            onClick={() => setChoice('separate')}
            aria-pressed={choice === 'separate'}
            className={cn(
              'w-full rounded-xl border p-4 text-left transition-colors',
              choice === 'separate' ? 'border-accent bg-accent/5' : 'border-grey-200 bg-white hover:bg-grey-50',
            )}
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-secondary">
              <UserX className="h-4 w-4 text-grey-500" />
              That is not me — I am new here
            </span>
            <span className="mt-1 block text-xs text-grey-500">
              Pick this if someone else used this email address before you. Their work stays theirs and you start
              with an empty Hub.
            </span>
          </button>
        </div>

        {choice === 'claim' && counts.length > 0 && (
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-warning/5 px-3 py-2 text-xs text-grey-600">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
            Only choose this if you recognise that work as yours. If this email address used to belong to a
            colleague, choose the other option.
          </p>
        )}

        {resolve.isError && (
          <p className="mt-4 text-xs text-error">
            That did not go through. Try again, or contact Solid Digital if it keeps failing.
          </p>
        )}

        <Button
          className="mt-6 w-full"
          disabled={!choice || resolve.isPending}
          onClick={() => choice && resolve.mutate(choice)}
        >
          {resolve.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Setting up your Hub…
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </div>
  );
}
